import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Loader2, ChevronDown, ChevronUp, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import { api } from '../../api/client'
import { DEFAULT_LARGE_TXN_THRESHOLD } from '../../constants'

const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0)
const fmtD = (d: string) => d ? new Date(d + 'T00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'

interface Txn {
  id: string; date: string; vendor: string; description: string; amount: number;
  category: string; account: string; flags?: string; pending?: boolean;
}
interface CatSpend { category: string; amount: number }
interface TxnResp {
  total: number; items: Txn[]; spending_by_category: CatSpend[]
  counts: { uncategorized: number; categorized: number; subscriptions: number; large: number }
}
interface CatResp { categories: string[]; colors: Record<string, string> }

type SortCol = 'date' | 'vendor' | 'account' | 'amount'

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'large', label: 'Large' },
  { key: 'uncategorized', label: 'Uncategorized' },
]

const THRESHOLD_OPTIONS = [50, 100, 200, 500, 1000]

export function ActivitiesPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get('tab') || 'all'
  const [search, setSearch] = useState('')
  const [data, setData] = useState<TxnResp | null>(null)
  const [cats, setCats] = useState<CatResp | null>(null)
  const [loading, setLoading] = useState(true)
  const [largeThreshold, setLargeThreshold] = useState(DEFAULT_LARGE_TXN_THRESHOLD)
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null)
  const [sortCol, setSortCol] = useState<SortCol>('date')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  const toggleSort = (col: SortCol) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(col); setSortDir(col === 'amount' ? 'desc' : 'asc') }
  }

  const sortTxns = (list: Txn[]) => {
    const dir = sortDir === 'asc' ? 1 : -1
    return [...list].sort((a, b) => {
      switch (sortCol) {
        case 'date': return dir * a.date.localeCompare(b.date)
        case 'vendor': return dir * a.vendor.localeCompare(b.vendor)
        case 'account': return dir * a.account.localeCompare(b.account)
        case 'amount': return dir * (Math.abs(a.amount) - Math.abs(b.amount))
        default: return 0
      }
    })
  }

  const SortIcon = ({ col }: { col: string }) => {
    if (sortCol !== col) return <ArrowUpDown className="h-3 w-3 text-slate-600" />
    return sortDir === 'asc' ? <ArrowUp className="h-3 w-3 text-indigo-400" /> : <ArrowDown className="h-3 w-3 text-indigo-400" />
  }

  const setTab = (tab: string) => {
    const params = new URLSearchParams(searchParams)
    if (tab === 'all') params.delete('tab'); else params.set('tab', tab)
    setSearchParams(params)
  }

  const loadTxns = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (activeTab !== 'all') params.set('tab', activeTab)
      if (search) params.set('search', search)
      if (largeThreshold > 0) params.set('large_threshold', String(largeThreshold))
      params.set('limit', '10000')
      const qs = params.toString()
      const r = await api.get<TxnResp>(`/api/transactions${qs ? '?' + qs : ''}`)
      setData(r)
    } catch (e) { console.error(e) }
    setLoading(false)
  }, [activeTab, search, largeThreshold])

  useEffect(() => { loadTxns() }, [loadTxns])
  useEffect(() => { api.get<CatResp>('/api/categories').then(setCats).catch(() => {}) }, [])

  const txns = data?.items ?? []
  const counts = data?.counts
  const catSpend = data?.spending_by_category ?? []
  const totalSpend = catSpend.reduce((s, c) => s + c.amount, 0)

  return (
    <div className="space-y-5">
      <h2 className="text-2xl font-bold text-white">Activities</h2>

      {/* Tab Bar */}
      <div className="flex gap-1 rounded-xl bg-slate-800 p-1 overflow-x-auto">
        {TABS.map((tab) => (
          <button key={tab.key} onClick={() => setTab(tab.key)}
            className={`flex-1 min-w-0 rounded-lg px-2 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${activeTab === tab.key ? 'bg-slate-700 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'}`}>
            {tab.label}
            {counts && tab.key === 'uncategorized' && counts.uncategorized > 0 && (
              <span className="ml-1.5 rounded-full bg-amber-500/20 px-2 py-0.5 text-xs text-amber-400">{counts.uncategorized}</span>
            )}
            {counts && tab.key === 'large' && counts.large > 0 && (
              <span className="ml-1.5 rounded-full bg-rose-500/20 px-2 py-0.5 text-xs text-rose-400">{counts.large}</span>
            )}
          </button>
        ))}
      </div>

      {/* Large threshold selector */}
      {activeTab === 'large' && (
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs text-slate-400">Threshold:</span>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-slate-500">$</span>
            <input
              type="number"
              min={1}
              value={largeThreshold}
              onChange={e => { const v = parseInt(e.target.value, 10); if (v > 0) setLargeThreshold(v) }}
              className="w-20 rounded-lg border border-slate-700 bg-slate-800 px-2.5 py-1 text-xs text-slate-200 text-center focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
          </div>
          <div className="flex gap-1.5">
            {THRESHOLD_OPTIONS.map(t => (
              <button key={t} onClick={() => setLargeThreshold(t)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  largeThreshold === t ? 'bg-rose-600 text-white' : 'bg-slate-800 text-slate-400 border border-slate-700 hover:border-rose-500/50 hover:text-rose-300'
                }`}>
                ${t}+
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search transactions..."
        className="rounded-lg border border-slate-700 bg-slate-800 px-4 py-2.5 text-sm text-slate-200 placeholder-slate-500 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 w-full" />

      {/* Spending by Category — expandable with sortable transaction tables */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-indigo-500" /></div>
      ) : txns.length === 0 ? (
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/80 py-16 text-center">
          <p className="text-sm font-medium text-slate-200">No transactions found</p>
          <p className="mt-1 text-sm text-slate-500">Try adjusting your filters or sync your accounts.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {catSpend.map((c) => {
            const pct = totalSpend > 0 ? (c.amount / totalSpend) * 100 : 0
            const color = cats?.colors?.[c.category] || '#6366f1'
            const isCatOpen = expandedCategory === c.category
            const catTxns = isCatOpen ? sortTxns(txns.filter(t => t.category === c.category && t.amount < 0)) : []
            return (
              <div key={c.category} className="rounded-xl border border-slate-700/50 bg-slate-800/80 overflow-hidden">
                {/* Category header */}
                <button
                  onClick={() => setExpandedCategory(isCatOpen ? null : c.category)}
                  className="flex w-full items-center gap-4 p-4 text-left hover:bg-slate-800/50 transition-colors"
                >
                  <span className="inline-block h-3 w-3 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-200">{c.category}</p>
                    <div className="mt-1 h-1.5 rounded-full bg-slate-700 overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-right">
                      <p className="font-bold text-slate-200 tabular-nums">{fmt(c.amount)}</p>
                      <p className="text-[10px] text-slate-500">{pct.toFixed(0)}%</p>
                    </div>
                    {isCatOpen ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
                  </div>
                </button>
                {/* Expanded sortable transaction list */}
                {isCatOpen && catTxns.length > 0 && (
                  <div className="border-t border-slate-700/30 bg-slate-900/40 overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead>
                        <tr className="text-slate-500">
                          <th className="px-4 py-2 text-left font-medium cursor-pointer select-none hover:text-slate-300 transition-colors" onClick={() => toggleSort('date')}>
                            <span className="inline-flex items-center gap-1">Date <SortIcon col="date" /></span>
                          </th>
                          <th className="px-4 py-2 text-left font-medium cursor-pointer select-none hover:text-slate-300 transition-colors" onClick={() => toggleSort('vendor')}>
                            <span className="inline-flex items-center gap-1">Merchant <SortIcon col="vendor" /></span>
                          </th>
                          <th className="px-4 py-2 text-left font-medium cursor-pointer select-none hover:text-slate-300 transition-colors" onClick={() => toggleSort('account')}>
                            <span className="inline-flex items-center gap-1">Account <SortIcon col="account" /></span>
                          </th>
                          <th className="px-4 py-2 text-right font-medium cursor-pointer select-none hover:text-slate-300 transition-colors" onClick={() => toggleSort('amount')}>
                            <span className="inline-flex items-center gap-1 justify-end">Amount <SortIcon col="amount" /></span>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700/20">
                        {catTxns.map((t, j) => (
                          <tr key={t.id || j} className="text-slate-400 hover:bg-slate-800/40">
                            <td className="whitespace-nowrap px-4 py-2">{fmtD(t.date)}</td>
                            <td className="px-4 py-2">
                              <p className="text-slate-300">{t.vendor}</p>
                              <p className="truncate text-[10px] text-slate-600 max-w-[200px]">{t.description}</p>
                            </td>
                            <td className="px-4 py-2 text-slate-500">{t.account}</td>
                            <td className="whitespace-nowrap px-4 py-2 text-right font-medium text-rose-400 tabular-nums">{fmt(Math.abs(t.amount))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )
          })}
          {/* Total */}
          <div className="flex justify-between rounded-xl border border-slate-700/50 bg-slate-800/80 px-4 py-3 text-sm">
            <span className="text-slate-400">Total · {data?.total ?? 0} transactions</span>
            <span className="font-bold text-white">{fmt(totalSpend)}</span>
          </div>
        </div>
      )}
    </div>
  )
}
